/**
 * @fileoverview Assignment Details Modal Component
 * @description Modal for displaying detailed assignment information
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - Mantine Modal with accessibility
 */

import { Modal, Stack, Group, Text, Badge, Divider, Paper, ThemeIcon } from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconBuilding,
  IconStairs,
  IconDoor,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { DoctorAssignment } from '../../../types/doctor';

/**
 * Props for AssignmentDetailsModal component
 */
export interface AssignmentDetailsModalProps {
  /** Assignment data to display (null to hide modal) */
  assignment: DoctorAssignment | null;
  
  /** Callback when modal is closed */
  onClose: () => void;
  
  /** Whether modal is open */
  opened: boolean;
}

/**
 * Assignment Details Modal Component
 * @description Displays comprehensive assignment information in accessible modal
 * 
 * @example
 * ```tsx
 * function DoctorSchedulePage() {
 *   const [selectedAssignment, setSelectedAssignment] = useState<DoctorAssignment | null>(null);
 *   
 *   return (
 *     <>
 *       <WeeklyCalendarView onAssignmentClick={setSelectedAssignment} />
 *       <AssignmentDetailsModal
 *         assignment={selectedAssignment}
 *         opened={!!selectedAssignment}
 *         onClose={() => setSelectedAssignment(null)}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function AssignmentDetailsModal({
  assignment,
  onClose,
  opened,
}: AssignmentDetailsModalProps) {
  if (!assignment) return null;

  const date = new Date(assignment.date);
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          Assignment Details
        </Text>
      }
      size="md"
      centered
      aria-labelledby="assignment-details-title"
    >
      <Stack gap="md">
        {/* Status Badge */}
        <Group justify="center">
          <Badge size="lg" color={getStatusColor(assignment.status)} variant="light">
            {assignment.status}
          </Badge>
          {assignment.published && (
            <Badge size="lg" color="blue" variant="filled">
              Published
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Date & Time Section */}
        <Paper p="md" radius="md" withBorder bg="gray.0">
          <Stack gap="sm">
            <DetailRow
              icon={IconCalendar}
              label="Date"
              value={formattedDate}
              color="blue"
            />
            <DetailRow
              icon={IconClock}
              label="Session"
              value={assignment.sessionTime.name}
              color="teal"
            />
            <DetailRow
              icon={IconClock}
              label="Time"
              value={`${assignment.sessionTime.startTime} - ${assignment.sessionTime.endTime}`}
              color="teal"
            />
          </Stack>
        </Paper>

        {/* Location Section */}
        <Paper p="md" radius="md" withBorder bg="blue.0">
          <Stack gap="sm">
            <DetailRow
              icon={IconBuilding}
              label="Building"
              value={assignment.room.floor.building.name}
              color="indigo"
            />
            <DetailRow
              icon={IconStairs}
              label="Floor"
              value={`${assignment.room.floor.name} (Floor ${assignment.room.floor.number})`}
              color="grape"
            />
            <DetailRow
              icon={IconDoor}
              label="Room"
              value={`${assignment.room.number} - ${assignment.room.name}`}
              color="violet"
            />
          </Stack>
        </Paper>

        {/* Additional Info */}
        <Paper p="md" radius="md" withBorder>
          <Group gap={8}>
            <IconInfoCircle size={16} stroke={1.5} style={{ opacity: 0.6 }} aria-hidden="true" />
            <Text size="xs" c="dimmed">
              Assignment ID: {assignment.id.slice(0, 8)}...
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Modal>
  );
}

/**
 * Detail row component for consistent layout
 */
interface DetailRowProps {
  icon: React.ComponentType<any>;
  label: string;
  value: string;
  color: string;
}

function DetailRow({ icon: Icon, label, value, color }: DetailRowProps) {
  return (
    <Group wrap="nowrap" gap="sm">
      <ThemeIcon color={color} variant="light" size="md" radius="md" aria-hidden="true">
        <Icon size={16} stroke={1.5} />
      </ThemeIcon>
      <div style={{ flex: 1 }}>
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
        <Text size="sm" fw={600}>
          {value}
        </Text>
      </div>
    </Group>
  );
}

/**
 * Get badge color for assignment status
 * @param status - Assignment status
 * @returns Mantine color name
 */
function getStatusColor(status: string): string {
  switch (status.toUpperCase()) {
    case 'CONFIRMED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'CANCELLED':
      return 'red';
    case 'COMPLETED':
      return 'gray';
    default:
      return 'blue';
  }
}

